import { ButtonProps, Flex, FlexProps, Icon, Image, Text } from '@chakra-ui/react';
import { useAtom, useAtomValue } from 'jotai';
import React from 'react';
import { MdOutlineUpdate } from 'react-icons/md';

import { Checkmark } from '../../../assets/Images';
import Button from '../../../components/ui/buttons/Button';
import { ExternalDeal } from '../../../gql/generated/graphql';
import useStartSigning, { ActiveModal } from '../../../hooks/useStartSigning';
import { sameEmail } from '../../../utils/customer';
import { DealStateEnum } from '../../../utils/types/deal';
import VerifyAccountModal from '../../auth/components/VerifyAccountModal';
import { dashboardDealInfoAtom } from '../RootDashboard';
import { DealWithFarthestState } from '../utils';
import { documentsSignedAtom, isDevActiveAtom } from './devToolbarAtoms';
import InlineSigningList from './stepper/InlineSigningList';

const SigningModal = React.lazy(() => import('./SigningModal'));

export const REVIEW_AND_SIGN_BTN_ID = 'dashboard-review-and-sign';

interface SignCompleteProps {
  hasCobuyer: boolean;
  isCobuyer: boolean;
}

interface SigningModalsProps {
  deal: ExternalDeal;
  activeModal: ActiveModal;
  setActiveModal: (modal: ActiveModal) => void;
  refetchDeal: () => void;
  sentForSignatures: boolean;
  onSigningClose?: () => void;
}

const SigningModals = ({
  deal,
  activeModal,
  setActiveModal,
  refetchDeal,
  sentForSignatures,
  onSigningClose,
}: SigningModalsProps) => {
  return (
    <>
      {sentForSignatures && (
        <VerifyAccountModal
          deal={deal}
          open={activeModal === 'verify'}
          onClose={() => setActiveModal(null)}
          handleNextSubmit={() => {
            refetchDeal();
            setActiveModal('signing');
          }}
        />
      )}

      {sentForSignatures && (
        <React.Suspense fallback={null}>
          <SigningModal
            key={`${deal.id}-${deal.isCobuyer}`}
            deal={deal}
            isOpen={activeModal === 'signing'}
            onClose={() => {
              setActiveModal(null);
              refetchDeal();
              onSigningClose?.();
            }}
          />
        </React.Suspense>
      )}
    </>
  );
};

const SignComplete = ({ hasCobuyer, isCobuyer }: SignCompleteProps) => {
  return (
    <Flex alignItems="center" gap="7px">
      <Image src={Checkmark} boxSize="20px" />
      <Text textTransform="uppercase" fontSize="13px" fontWeight="semibold">
        {hasCobuyer
          ? isCobuyer
            ? 'CO-BUYER E-SIGNING COMPLETE (WAITING FOR BUYER TO SIGN)'
            : 'BUYER E-SIGNING COMPLETE (WAITING FOR CO-BUYER TO SIGN)'
          : 'E-SIGNING COMPLETE'}
      </Text>
    </Flex>
  );
};

type SignButtonsProps = FlexProps & {
  deal: DealWithFarthestState;
  buttonProps?: ButtonProps;
};

const SignButtons = ({ deal, buttonProps, ...rest }: SignButtonsProps) => {
  const isDevActive = useAtomValue(isDevActiveAtom);
  const devDocumentsSigned = useAtomValue(documentsSignedAtom);
  const [dealId, setDealId] = useAtom(dashboardDealInfoAtom);
  const {
    startSigning,
    activeModal,
    setActiveModal,
    refetchDeal,
    signingIsCobuyer,
    renderErrorModals,
  } = useStartSigning(deal);

  const shareEmail = sameEmail(deal?.customer, deal?.cobuyer);
  const sentForSignatures = deal?.state === DealStateEnum.SentForSignatures;
  const isCobuyer = deal?.isCobuyer ?? false;
  const hasCobuyer = !!deal?.cobuyer;
  const startedSigning = !!(isCobuyer
    ? deal?.cobuyer?.signature_data
    : deal?.customer?.signature_data);
  const doneSigning =
    (isCobuyer ? deal?.cobuyer?.finished_signing : deal?.customer?.finished_signing) ||
    (isDevActive && devDocumentsSigned);

  const buttonCopy = !sentForSignatures
    ? 'Preparing Documents'
    : startedSigning
    ? 'Continue Signing'
    : 'Review & Sign';

  const resetAtomForSharedEmail = () => {
    if (shareEmail && dealId?.id) {
      setDealId({ id: dealId.id, isCobuyer: false });
    }
  };

  const signingDeal = shareEmail ? { ...deal, isCobuyer: signingIsCobuyer } : deal;

  const sharedModals = (
    <>
      {renderErrorModals}
      <SigningModals
        deal={signingDeal}
        activeModal={activeModal}
        setActiveModal={setActiveModal}
        refetchDeal={refetchDeal}
        sentForSignatures={sentForSignatures}
        onSigningClose={resetAtomForSharedEmail}
      />
    </>
  );

  if (shareEmail) {
    return (
      <>
        {sharedModals}
        <InlineSigningList deal={deal} startSigning={startSigning} />
      </>
    );
  }

  if (doneSigning) {
    return (
      <>
        {sharedModals}
        <Flex w="full" justifyContent="flex-start" {...rest}>
          <SignComplete hasCobuyer={hasCobuyer} isCobuyer={isCobuyer} />
        </Flex>
      </>
    );
  }

  return (
    <>
      {sharedModals}
      <Flex flexDir={{ base: 'column', md: 'row' }} alignItems="center" gap="10px" {...rest}>
        <Button
          {...buttonProps}
          id={REVIEW_AND_SIGN_BTN_ID}
          trackClick={REVIEW_AND_SIGN_BTN_ID}
          leftIcon={!sentForSignatures ? <Icon as={MdOutlineUpdate} w={6} h={6} /> : undefined}
          py={!sentForSignatures ? { base: 7, md: 'unset' } : undefined}
          textTransform="uppercase"
          isDisabled={!sentForSignatures}
          onClick={() => startSigning()}
        >
          {buttonCopy}
        </Button>
      </Flex>
    </>
  );
};

export default SignButtons;
